import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HelperText } from '@cbhq/cds-web/controls/HelperText';
import { VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { SectionHeader } from '@cbhq/cds-web/section-header/SectionHeader';
import { Text } from '@cbhq/cds-web/typography/Text';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';
import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import { InviteEmailSection } from '../components/InviteTray/InviteEmailSection';
import { InviteTray } from '../components/InviteTray/InviteTray';
import { ProductAccessFooter } from '../components/InviteTray/ProductAccessFooter';
import { ProductAccessGroup } from '../components/InviteTray/ProductAccessGroup';
import { PRODUCTS } from '../components/InviteTray/productCatalog';
import { useInviteEmailField } from '../components/InviteTray/useInviteEmailField';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useHandleInviteFailure } from '../useHandleInviteFailure';
import { useInviteSubmission } from '../useInviteSubmission';
import { useProductSelection } from '../useProductSelection';
import { hasGatedRole } from '../utils/stepUp';
import { useRoles } from '../utils/useRoles';

const messages = defineMessages({
  accessHeading: {
    id: 'Team.inviteTray.accessHeading',
    defaultMessage: 'Access',
    description: 'Heading for the product access section of the invite tray',
  },
  inviteSuccess: {
    id: 'Team.singleLEInviteTray.inviteSuccess',
    defaultMessage: 'Invite sent',
    description: 'Toast shown after a single legal entity invite is successfully sent',
  },
  inviteError: {
    id: 'Team.singleLEInviteTray.inviteError',
    defaultMessage: 'Error sending invite',
    description: 'Toast shown when sending a single legal entity invite fails',
  },
  productAccessRequiredError: {
    id: 'Team.inviteTray.productAccessRequiredError',
    defaultMessage: 'You must select at least one product',
    description: 'Inline error shown when no product access has been selected on submit',
  },
});

type SingleLEInviteTrayParams = {
  onClose: () => void;
};

export function SingleLEInviteTray({ onClose }: SingleLEInviteTrayParams) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const trayRef = useRef<TrayRefProps>(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const { selectedOrganization } = useOrganization();
  const userProfile = useUserProfile();
  const currentUserEmail = userProfile?.email;
  const organizationId = selectedOrganization?.organizationId ?? '';
  const isWorkspaceRoleTrayTreatment = useIsTreatment(AppExperiments.t0018_74bfb564);

  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );

  const {
    email,
    emailError,
    setEmailError,
    handleEmailChange,
    validate: validateEmail,
  } = useInviteEmailField({
    currentUserEmail,
    members: orgMembersData?.members,
    pendingInvites: orgMembersData?.pendingInvites,
  });
  const [accessError, setAccessError] = useState<string | null>(null);

  const localizedProducts = useMemo(
    () =>
      PRODUCTS.map(({ titleMessage, trayTitleMessage, descriptionMessage, ...product }) => ({
        ...product,
        title: formatMessage(isWorkspaceRoleTrayTreatment ? trayTitleMessage : titleMessage),
        description: formatMessage(descriptionMessage),
        hideIcon: isWorkspaceRoleTrayTreatment && Boolean(product.hideIconInTray),
      })),
    [formatMessage, isWorkspaceRoleTrayTreatment],
  );

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const { rolesByProductPerEntity } = useRoles();
  const leRolesByProduct = useMemo(
    () => rolesByProductPerEntity[legalEntityIds[0] ?? ''] ?? {},
    [rolesByProductPerEntity, legalEntityIds],
  );

  const filteredLocalizedProducts = useMemo(
    () =>
      localizedProducts.filter(({ productKey }) => (leRolesByProduct[productKey]?.length ?? 0) > 0),
    [localizedProducts, leRolesByProduct],
  );

  const {
    accessRows,
    selectedRoles,
    selectedCount,
    totalCount,
    allProductsSelected,
    handleSelectAll,
    handleDeselectAll,
  } = useProductSelection({
    products: filteredLocalizedProducts,
    rolesByProduct: leRolesByProduct,
  });

  useEffect(() => {
    if (selectedRoles.length > 0) {
      setAccessError(null);
    }
  }, [selectedRoles.length]);

  const hasUnsavedInvite = useMemo(
    () => email.length > 0 || selectedRoles.length > 0,
    [email.length, selectedRoles.length],
  );

  const isGatedMutation = useMemo(() => hasGatedRole(selectedRoles), [selectedRoles]);

  const { submit, stepUpModalProps, resetStepUpRecovery, handleStepUpProofTokenError } =
    useStepUpSubmission({ isGatedRoleMutation: isGatedMutation });

  const handleInviteSuccess = useCallback(() => {
    resetStepUpRecovery();
    toast.show(formatMessage(messages.inviteSuccess));
    onClose();
  }, [formatMessage, onClose, resetStepUpRecovery, toast]);

  const surfaceInviteFailure = useHandleInviteFailure({
    setEmailError,
    toast,
    genericErrorMessage: messages.inviteError,
  });

  const handleInviteFailure = useCallback(
    (error: unknown) => {
      // A rejected step-up proof token re-opens the 2FA challenge, so it must be
      // checked before surfaceInviteFailure turns the error into user-facing copy.
      if (handleStepUpProofTokenError(error)) {
        return;
      }
      surfaceInviteFailure(error);
    },
    [handleStepUpProofTokenError, surfaceInviteFailure],
  );

  const { isSubmitting, submitInvite } = useInviteSubmission({
    organizationId,
    email,
    selectedRoles,
    legalEntityIds,
    onSuccess: handleInviteSuccess,
    onFailure: handleInviteFailure,
  });

  const handleInvite = useCallback(() => {
    const nextEmailError = validateEmail();
    const nextAccessError =
      selectedRoles.length === 0 || legalEntityIds.length === 0
        ? formatMessage(messages.productAccessRequiredError)
        : null;

    setEmailError(nextEmailError);
    setAccessError(nextAccessError);

    if (nextEmailError != null || nextAccessError != null) {
      return;
    }

    submit(submitInvite);
  }, [
    formatMessage,
    legalEntityIds.length,
    selectedRoles.length,
    setEmailError,
    submit,
    submitInvite,
    validateEmail,
  ]);

  const handleBeforeClose = useCallback(() => {
    if (hasUnsavedInvite) {
      setAlertVisible(true);
      return;
    }
    trayRef.current?.close();
  }, [hasUnsavedInvite]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    trayRef.current?.close();
  }, []);

  return (
    <>
      <InviteTray
        trayRef={trayRef}
        onClose={onClose}
        onInvite={handleInvite}
        beforeClose={handleBeforeClose}
        isSubmitting={isSubmitting}
      >
        <InviteEmailSection
          email={email}
          onEmailChange={handleEmailChange}
          errorMessage={emailError}
        />

        <VStack width="100%" gap={2}>
          <SectionHeader
            title={<Text font="headline">{formatMessage(messages.accessHeading)}</Text>}
            paddingX={0}
            paddingY={0}
          />
          <ProductAccessGroup
            rows={accessRows}
            showWorkspaceRoleDivider={isWorkspaceRoleTrayTreatment}
            footer={
              <ProductAccessFooter
                selectedCount={selectedCount}
                totalCount={totalCount}
                allSelected={allProductsSelected}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
              />
            }
          />
          {accessError != null && <HelperText color="fgNegative">{accessError}</HelperText>}
        </VStack>
      </InviteTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="invite"
        visible={alertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
